// Utility functions for loading and managing sound tracks

// Get all audio files from all directories
export const getAllSounds = () => {
  const directories = ['clean', 'crushed', 'granular', 'spectral'];
  const sounds = [];

  directories.forEach(dir => {
    // AM tracks
    for (let i = 1; i <= 8; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - AM ${i}.wav`,
        type: 'AM',
        number: i,
        category: dir
      });
    }

    // FL tracks
    for (let i = 1; i <= 5; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - FL ${i}.wav`,
        type: 'FL',
        number: i,
        category: dir
      });
    }

    // FX tracks
    for (let i = 1; i <= 4; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - FX ${i}.wav`,
        type: 'FX',
        number: i,
        category: dir
      });
    }

    // GL tracks
    for (let i = 1; i <= 13; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - GL ${i}.wav`,
        type: 'GL',
        number: i,
        category: dir
      });
    }

    // IN tracks
    for (let i = 1; i <= 3; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - IN ${i}.wav`,
        type: 'IN',
        number: i,
        category: dir
      });
    }

    // PD tracks
    for (let i = 1; i <= 5; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - PD ${i}.wav`,
        type: 'PD',
        number: i,
        category: dir
      });
    }

    // PN tracks
    for (let i = 1; i <= 3; i++) {
      sounds.push({
        path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - PN ${i}.wav`,
        type: 'PN',
        number: i,
        category: dir
      });
    }

    // SB tracks (only in clean, crushed, and spectral)
    if (dir !== 'granular') {
      for (let i = 1; i <= 3; i++) {
        sounds.push({
          path: `${process.env.PUBLIC_URL}/sounds/${dir}/headache - SB ${i}.wav`,
          type: 'SB',
          number: i,
          category: dir
        });
      }
    }
  });

  return sounds;
};

// Get random tracks with max 1 SB track
export const getRandomTracks = (allSounds, count = 8) => {
  const sbTracks = allSounds.filter(sound => sound.type === 'SB');
  const nonSbTracks = allSounds.filter(sound => sound.type !== 'SB');
  
  const selectedTracks = [];
  
  // Add max 1 SB track (randomly decide if we include one)
  if (Math.random() > 0.3) { // 70% chance to include an SB track
    const randomSB = sbTracks[Math.floor(Math.random() * sbTracks.length)];
    selectedTracks.push(randomSB);
  }
  
  // Fill remaining slots with non-SB tracks
  const remainingSlots = count - selectedTracks.length;
  const shuffledNonSb = [...nonSbTracks].sort(() => Math.random() - 0.5);
  
  for (let i = 0; i < remainingSlots && i < shuffledNonSb.length; i++) {
    selectedTracks.push(shuffledNonSb[i]);
  }
  
  // Shuffle the final selection
  return selectedTracks.sort(() => Math.random() - 0.5);
};

// Mix and download current tracks as a single audio file
export const mixAndDownloadTracks = async (tracks) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffers = [];
    let maxDuration = 0;
    
    // Load all audio files
    for (const track of tracks) {
      try {
        const response = await fetch(track.path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        audioBuffers.push(audioBuffer);
        maxDuration = Math.max(maxDuration, audioBuffer.duration);
      } catch (error) {
        console.warn(`Failed to load track: ${track.path}`, error);
      }
    }
    
    if (audioBuffers.length === 0) {
      throw new Error('No audio tracks could be loaded');
    }
    
    // Create output buffer
    const sampleRate = audioBuffers[0].sampleRate;
    const outputBuffer = audioContext.createBuffer(2, maxDuration * sampleRate, sampleRate);
    
    // Mix all tracks
    for (let channel = 0; channel < 2; channel++) {
      const outputData = outputBuffer.getChannelData(channel);
      
      for (const audioBuffer of audioBuffers) {
        const inputData = audioBuffer.getChannelData(Math.min(channel, audioBuffer.numberOfChannels - 1));
        
        for (let i = 0; i < inputData.length; i++) {
          outputData[i] = (outputData[i] || 0) + inputData[i] * 0.5; // Reduce volume to prevent clipping
        }
      }
    }
    
    // Convert to WAV
    const wavBlob = audioBufferToWav(outputBuffer);
    
    // Create download
    const url = URL.createObjectURL(wavBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `headache-mix-${Date.now()}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    audioContext.close();
  } catch (error) {
    console.error('Error mixing and downloading tracks:', error);
    alert('Failed to download mixed audio. Please try again.');
  }
};

// Convert AudioBuffer to WAV blob
const audioBufferToWav = (buffer) => {
  const length = buffer.length;
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const bufferSize = 44 + dataSize;
  
  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  
  // Convert samples
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample * 0x7FFF, true);
      offset += 2;
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};